import dayjs from 'dayjs'
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Typography, useTheme, useMediaQuery, IconButton, Drawer, Dialog } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import CollectionsIcon from '@mui/icons-material/Collections'
import CloseIcon from '@mui/icons-material/Close'
import SwipeIcon from '@mui/icons-material/Swipe'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Zoom } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/zoom'
import VisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/VisitReportsComponent'
import PastReportErrorBoundary from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/PastReportErrorBoundary'
import { LSPastReportsSkeleton } from 'src/consumer/components/LiveSessionPage/LiveSessionPageStyles'
import { REDUX_STATUS_LOADING } from 'src/consumer/constants'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { selectEmrPastReports } from 'src/reducers/emr/emrReducer'
import { getPastPatientReports } from 'src/reducers/emr/services/pastPatientReports'
import PatientVisitSummaryHandler from 'src/mentor/components/PrescriptionMakingPage/consultationSummary/PatientVisitSummaryHandler'
import { parseReportTimestamp } from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'

const batchedVitalsCache = new Map()
const batchedVitalsInFlightRequests = new Map()

/** Get timestamp for sorting (must match VisitReportsComponent logic) */
function getReportTimestamp(report) {
  if (report?.apiParam === 'meta-prescription') {
    return report?.prescriptionData?.startTime || report?.prescriptionData?.createdTime || report?.prescriptionData?.modifiedTime
  }
  if (report?.type === 'therapy-report-response') {
    return report?.therapyReport?.createdTime || report?.therapyReport?.modifiedTime
  }
  if (report?.type === 'screening-report-response') {
    return report?.screeningReport?.createdTime || report?.screeningReport?.modifiedTime
  }
  if (report?.type === 'assessment-report-response') {
    const runs = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
    const latest = runs.length > 0 ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0] : null
    return latest?.completedAt || report?.assessmentReport?.createdTime || report?.assessmentReport?.modifiedTime
  }
  return report?.createdTime || report?.modifiedTime
}

function sortReportsByTime(reports) {
  return [...(reports || [])].sort((a, b) => {
    const parsedA = parseReportTimestamp(getReportTimestamp(a))
    const parsedB = parseReportTimestamp(getReportTimestamp(b))
    const momentA = parsedA?.isValid() ? parsedA : dayjs(getReportTimestamp(a))
    const momentB = parsedB?.isValid() ? parsedB : dayjs(getReportTimestamp(b))
    return momentB.valueOf() - momentA.valueOf()
  })
}

function isNonEmptyValue(value) {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  if (value && typeof value === 'object') return Object.keys(value).length > 0
  return value !== null && value !== undefined
}

function mergeArrayValues(existingValue, incomingValue) {
  const existing = Array.isArray(existingValue) ? existingValue : []
  const incoming = Array.isArray(incomingValue) ? incomingValue : []
  if (existing.length === 0) return incoming
  if (incoming.length === 0) return existing
  const seen = new Set()
  const merged = []
  ;[...existing, ...incoming].forEach((item) => {
    const key =
      item && typeof item === 'object'
        ? JSON.stringify(item)
        : String(item)
    if (!seen.has(key)) {
      seen.add(key)
      merged.push(item)
    }
  })
  return merged
}

function mergeEmrSectionDataList(existingValue, incomingValue) {
  const existing = Array.isArray(existingValue) ? existingValue : []
  const incoming = Array.isArray(incomingValue) ? incomingValue : []
  if (existing.length === 0) return incoming
  if (incoming.length === 0) return existing
  const bySectionCode = new Map()
  ;[...existing, ...incoming].forEach((item) => {
    const key = item?.sectionCode
    if (!key) return
    const prev = bySectionCode.get(key)
    if (!prev) {
      bySectionCode.set(key, item)
      return
    }
    const prevCount = Array.isArray(prev?.subSections) ? prev.subSections.length : 0
    const currCount = Array.isArray(item?.subSections) ? item.subSections.length : 0
    if (currCount > prevCount) bySectionCode.set(key, item)
  })
  return Array.from(bySectionCode.values())
}

function getPrescriptionContentScore(report) {
  const data = report?.prescriptionData || {}
  const arrLen = (x) => (Array.isArray(x) ? x.length : 0)
  return (
    arrLen(data.complaints) +
    arrLen(data.diagnosis) +
    arrLen(data.investigations) +
    arrLen(data.drugInfoList) +
    arrLen(data.guidanceAndAdvices) +
    arrLen(data.prescriptionAdvices) +
    arrLen(data.instructions) +
    arrLen(data.healthInstructions) +
    arrLen(data.symptomsAndObservations) +
    arrLen(data.healthObservations) +
    arrLen(data.emrSectionDataList) +
    (typeof data.freeFormRichText === 'string' ? data.freeFormRichText.trim().length : 0)
  )
}

/** Match ReportTypeMarker timeline card colors for index intervention titles */
const INDEX_TYPE_COLORS = {
  Consultation: 'primary.main',
  Therapy: '#0d9488',
  Assessment: '#2563eb',
  Screening: '#d97706',
}

/** For assessment: prefer first run's scale title; fallback to report title. One list entry = one report (all runs inside it). */
function getAssessmentDisplayTitle(report) {
  const runs = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
  const run = runs[0]
  return run?.clinicalAssessmentTitle ?? run?.psychAssessmentTitle ?? run?.assessmentTitle ?? report?.assessmentReport?.title ?? 'Assessment'
}

function getEventTypeAndPractitioner(report) {
  let eventType = 'Report'
  let eventTypeLabel = 'Report'
  let practitionerName = ''
  if (report?.apiParam === 'meta-prescription') {
    eventType = 'Consultation'
    eventTypeLabel = 'Consultation'
    practitionerName = report?.prescriptionData?.doctorName || report?.prescriptionData?.mentorName || ''
  } else if (report?.type === 'therapy-report-response') {
    eventType = 'Therapy'
    eventTypeLabel = 'Therapy'
    practitionerName = report?.therapyReport?.mentorName || report?.therapyReport?.therapistName || ''
  } else if (report?.type === 'assessment-report-response') {
    eventType = 'Assessment'
    eventTypeLabel = getAssessmentDisplayTitle(report)
    practitionerName = report?.assessmentReport?.mentorName || report?.assessmentReport?.therapistName || ''
  } else if (report?.type === 'screening-report-response') {
    eventType = 'Screening'
    eventTypeLabel = 'Screening'
    practitionerName = report?.screeningReport?.mentorName || report?.screeningReport?.therapistName || ''
  }
  return { eventType, eventTypeLabel, practitionerName: practitionerName?.trim() || '—' }
}

function getBookedEventIdForReport(report) {
  return (
    report?.prescriptionData?.bookedEventId ||
    report?.therapyReport?.bookedEventId ||
    report?.assessmentReport?.bookedEventId ||
    report?.screeningReport?.bookedEventId ||
    null
  )
}

export default function LSPastVisitReportsComponent({
  patientId,
  referenceBookedEventId,
  screeningSections,
  setIsPrintingPastHistoryReports,
  allVitalsOfThisPatient,
  mentorPrivateNotes,
  addTopMargins = false,
  prescriptionViewSettings,
  showOnlyTherapies = false,
  onlyPrescription = false,
  skeletonNumber = 5,
  hidePrintButton = false,
  hideSendToDoctorButton = false,
  customLoader,
  typeOfReportsToShowArray = ['prescription', 'therapy', 'screening', 'assessment'],
  patientHistoryGetApiSignature,
  methodCallWhenNoReportDataIsFound = ()=>{},
  role: propRole = 'therapist', // Default to 'therapist' for backward compatibility
  clinicId, // Optional clinicId for fetching vitals
  hideVisitSummary = false, // Hide Therapy History and Consultation History summary blocks
  hidePrivateNotes = false, // Hide private notes fetching
  doctorSpecializations = null, // Doctor specializations for fetching screening sections
  visitType = null, // Visit type ('new' or 'repeat') for fetching screening sections
  showIndexSidebar = false, // When true, show left index (visit list) and sync scroll; hidden on mobile
  galleryControlRef = null, // Optional ref the parent populates with { open } to trigger the gallery from its own header
  hideInlineGalleryButton = false, // Suppress the built-in sticky gallery button when the parent provides one in its header
  galleryZIndex = undefined, // Lift the fullscreen gallery above a high-z host (e.g. the chamber history drawer)
  compact = false, // Force the mobile/stacked patient-history layout on desktop (drops the timeline vertical line)
}) {
  // Normalize screeningSections to always be an array
  const normalizedScreeningSections = React.useMemo(() => {
    if (Array.isArray(screeningSections)) {
      return screeningSections
    }
    return []
  }, [screeningSections])
  
  // Extract mentorId from patientHistoryGetApiSignature if it contains practitioner-id
  const mentorId = useMemo(() => {
    if (patientHistoryGetApiSignature) {
      const match = patientHistoryGetApiSignature.match(/practitioner-id=([^&]+)/)
      return match ? match[1] : null
    }
    return null
  }, [patientHistoryGetApiSignature])
  // const [loading, setLoading] = useState(false)
  const [dateMap, setDateMap] = useState({})
  const [galleryOpen, setGalleryOpen] = useState(false)
  const [galleryIndex, setGalleryIndex] = useState(0)
  const [showSwipeHint, setShowSwipeHint] = useState(false)

  const openGallery = useCallback(() => {
    setGalleryIndex(0)
    setGalleryOpen(true)
  }, [])

  // Let a parent (e.g. the drawer's own header) trigger the gallery via a control ref.
  useEffect(() => {
    if (!galleryControlRef) return undefined
    galleryControlRef.current = { open: openGallery }
    return () => {
      if (galleryControlRef) galleryControlRef.current = null
    }
  }, [galleryControlRef, openGallery])

  // Briefly show a "swipe to browse" hint each time the gallery opens.
  useEffect(() => {
    if (!galleryOpen) return undefined
    setShowSwipeHint(true)
    const timer = setTimeout(() => setShowSwipeHint(false), 3000)
    return () => clearTimeout(timer)
  }, [galleryOpen])
  const [vitalsByBookedEventId, setVitalsByBookedEventId] = useState({})
  const [batchedVitalsLoading, setBatchedVitalsLoading] = useState(false)
  const [batchedVitalsReady, setBatchedVitalsReady] = useState(false)

  const dispatch = useDispatch()

  const theme = useTheme()
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down('sm')) || compact
  const isFloatingMode = useMediaQuery(theme.breakpoints.down('md')) || compact
  const [indexDrawerOpen, setIndexDrawerOpen] = useState(false)
  const pastReportStoreData = useSelector(selectEmrPastReports)
  // API returns { code, message, data }. pastReportData is that data object (data["assessment-report-response"], etc.).
  const pastReportData = pastReportStoreData?.data
  const loading = pastReportStoreData?.status === REDUX_STATUS_LOADING
  const contentScrollRef = useRef(null)
  const [activeSectionId, setActiveSectionId] = useState(null)
  const SIDEBAR_WIDTH = 180

  // Compute patientVisitSummary from pastReportData based on role
  const computePatientVisitSummaryFromReports = useCallback((reports, currentRole) => {
    if (!reports) return null
    
    const therapyReports = reports['therapy-report-response'] || []
    const prescriptionReports = reports['meta-prescription'] || []
    
    // Get first and last prescription dates
    const prescriptionDates = prescriptionReports
      .map(r => r.prescriptionData?.startTime || r.prescriptionData?.createdTime || r.prescriptionData?.modifiedTime)
      .filter(Boolean)
      .sort()
    
    const firstPrescriptionDate = prescriptionDates[0]
    const lastPrescriptionDate = prescriptionDates[prescriptionDates.length - 1]
    
    // Get first and last therapy dates
    const therapyDates = therapyReports
      .map(r => r.therapyReport?.createdTime || r.therapyReport?.modifiedTime || r.therapyReport?.startTime)
      .filter(Boolean)
      .sort()
    
    const firstTherapyDate = therapyDates[0]
    const lastTherapyDate = therapyDates[therapyDates.length - 1]
    const latestTherapyVisitTime = therapyDates[therapyDates.length - 1] // Most recent therapy visit
    
    if (currentRole === 'doctor') {
      // For doctor role: Consultation Summary + Therapy Summary
      const consultationSessions = prescriptionReports.length
      const therapySessionsConducted = therapyReports.length
      // Get referring therapist name from therapy reports
      const referringPractitionerName = therapyReports[0]?.therapyReport?.mentorName || 
                                        therapyReports[0]?.therapyReport?.therapistName || null
      
      return {
        // For Consultation Summary card
        patientSince: firstPrescriptionDate, // First consultation date
        lastVisitDate: lastPrescriptionDate, // Last consultation date
        sessionsCompletedForCurrentEventTypeForCurrentMentor: consultationSessions, // Total consultations
        
        // For Therapy Summary card
        inTherapySince: firstTherapyDate, // When therapy started
        lastTherapyVisitSince: lastTherapyDate, // Last therapy visit date
        latestTherapyVisitTime: latestTherapyVisitTime, // Latest therapy session time
        therapySessionsConducted: therapySessionsConducted, // Total therapy sessions
        referringPractitionerName: referringPractitionerName, // Consulting therapist name
      }
    } else {
      // For therapist role: Therapy History + Consultation History
      const therapySessionsConducted = therapyReports.length
      // Get referring doctor name from prescriptions
      const referringPractitionerName = prescriptionReports[0]?.prescriptionData?.doctorName || 
                                        prescriptionReports[0]?.prescriptionData?.mentorName || null
      
      return {
        // For Therapy History card
        firstVisitDateForCurrentPractitioner: firstTherapyDate || firstPrescriptionDate, // First therapy date or first prescription if no therapy
        sessionsCompletedForCurrentEventTypeForCurrentMentor: therapySessionsConducted, // Total therapy sessions
        lastVisitDate: lastTherapyDate || lastPrescriptionDate, // Last therapy date or last prescription
        inTherapySince: firstTherapyDate, // When therapy started
        therapySessionsConducted: therapySessionsConducted, // Total therapy sessions
        
        // For Consultation History card
        referringPractitionerName: referringPractitionerName, // Referring doctor name
      }
    }
  }, [])

  const computedPatientVisitSummary = useMemo(() => {
    if (pastReportData) return computePatientVisitSummaryFromReports(pastReportData, propRole)
    return null
  }, [pastReportData, computePatientVisitSummaryFromReports, propRole])

  const sortedDates = useMemo(() => {
    if (!dateMap || Object.keys(dateMap).length === 0) return []
    return Object.keys(dateMap).sort((a, b) => {
      const parseDate = (dateStr) => dayjs(dateStr.split(',')[0], 'DD/MM/YYYY')
      return parseDate(b).valueOf() - parseDate(a).valueOf()
    })
  }, [dateMap])

  const { indexEntries, dateToSectionIds, groupedByVisit } = useMemo(() => {
    if (!showIndexSidebar || sortedDates.length === 0) {
      return { indexEntries: [], dateToSectionIds: {}, groupedByVisit: [] }
    }
    const entries = []
    const dateToIds = {}
    const grouped = []
    sortedDates.forEach((date, dateIndex) => {
      const reports = sortReportsByTime(dateMap[date] || [])
      const ids = []
      const visitEntries = []
      reports.forEach((report, reportIndex) => {
        const id = `history-s-${dateIndex}-${reportIndex}`
        ids.push(id)
        const { eventType, eventTypeLabel, practitionerName } = getEventTypeAndPractitioner(report)
        entries.push({ id, date, eventType, eventTypeLabel, practitionerName })
        visitEntries.push({ id, eventType, eventTypeLabel, practitionerName })
      })
      dateToIds[date] = ids
      grouped.push({
        visitNo: sortedDates.length - dateIndex,
        date,
        entries: visitEntries,
      })
    })
    return { indexEntries: entries, dateToSectionIds: dateToIds, groupedByVisit: grouped }
  }, [showIndexSidebar, sortedDates, dateMap])

  // Newest-first list of visit dates, shared by the stacked view and the swipe gallery.
  const sortedDatesList = useMemo(() => {
    if (sortedDates.length > 0) return sortedDates
    return Object.keys(dateMap || {}).sort((a, b) => {
      const parseDate = (s) => dayjs(s.split(',')[0], 'DD/MM/YYYY')
      return parseDate(b).valueOf() - parseDate(a).valueOf()
    })
  }, [sortedDates, dateMap])

  // One renderer reused by the stacked list and each gallery slide, so a report looks identical
  // in both. `total` lets us compute the descending Visit number.
  const renderVisitReportsForDate = (date, index, total) => (
    <PastReportErrorBoundary label={`Reports from ${date}`}>
      <VisitReportsComponent
        visitNo={total - index}
        date={date}
        allVitalsOfThisPatient={allVitalsOfThisPatient}
        vitalsByBookedEventId={vitalsByBookedEventId}
        useBatchedVitals
        batchedVitalsLoading={batchedVitalsLoading}
        batchedVitalsReady={batchedVitalsReady}
        reports={dateMap?.[date]}
        screeningSections={normalizedScreeningSections}
        setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
        mentorPrivateNotes={mentorPrivateNotes}
        prescriptionViewSettings={prescriptionViewSettings}
        hidePrintButton={hidePrintButton}
        hideSendToDoctorButton={hideSendToDoctorButton}
        patientId={patientId}
        clinicId={clinicId}
        role={propRole}
        hidePrivateNotes={hidePrivateNotes}
        mentorId={mentorId}
        patientHistoryGetApiSignature={patientHistoryGetApiSignature}
        doctorSpecializations={doctorSpecializations}
        visitType={visitType}
        forceStacked={compact}
        sectionIds={dateToSectionIds[date]}
      />
    </PastReportErrorBoundary>
  )

  useEffect(() => {
    if (!showIndexSidebar || indexEntries.length === 0 || !contentScrollRef.current) return
    const container = contentScrollRef.current
    const observers = new Map()
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id
          if (!id || !id.startsWith('history-s-')) return
          if (entry.isIntersecting) {
            const ratio = entry.intersectionRatio
            observers.set(id, ratio)
          } else {
            observers.delete(id)
          }
        })
        if (observers.size === 0) return
        const best = [...observers.entries()].sort((a, b) => b[1] - a[1])[0]
        if (best) setActiveSectionId(best[0])
      },
      { root: container, rootMargin: '-20% 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] }
    )
    indexEntries.forEach((e) => {
      const el = document.getElementById(e.id)
      if (el) observer.observe(el)
    })
    return () => observer.disconnect()
  }, [showIndexSidebar, indexEntries])

  // incomingData = API response's data object (data["assessment-report-response"], data["therapy-report-response"], etc.).
  function setDatewiseMapData(incomingData) {
    // const prescriptions = incomingData?.['prescription']
    // const therapyReports = incomingData?.['therapy-report']
    // const screeningReports = incomingData?.['screening-report']
    const metaPrescriptions = incomingData?.['meta-prescription'] || []
    const therapyReportResponse =
      incomingData?.['therapy-report-response']?.map((response) => {
        return { ...response, type: 'therapy-report-response' }
      }) || []
    const assessmentReportResponse =
      incomingData?.['assessment-report-response']?.map((response) => {
        return { ...response, type: 'assessment-report-response' }
      }) || []
    const screeningReportResponse =
      incomingData?.['screening-report-response']?.map((response) => {
        return { ...response, type: 'screening-report-response' }
      }) || []

    let allReports = [
      // ...prescriptions,
      // ...therapyReports,
      // ...screeningReports,
      // ...metaPrescriptions,
      // ...therapyReportResponse,
      // ...assessmentReportResponse,
      // ...screeningReportResponse,
    ]

    if (typeOfReportsToShowArray.length > 0) {
      if (typeOfReportsToShowArray.includes('prescription')) {
        allReports = [...allReports, ...metaPrescriptions]
      }
      if (typeOfReportsToShowArray.includes('therapy')) {
        allReports = [...allReports, ...therapyReportResponse]
      }
      if (typeOfReportsToShowArray.includes('assessment')) {
        allReports = [...allReports, ...assessmentReportResponse]
      }
      if (typeOfReportsToShowArray.includes('screening')) {
        allReports = [...allReports, ...screeningReportResponse]
      }
    }

    if (showOnlyTherapies) {
      allReports = [
        // ...prescriptions,
        // ...therapyReports,
        // ...screeningReports,
        ...therapyReportResponse,
      ]
    }
    if (onlyPrescription) {
      allReports = [
        ...metaPrescriptions,
        // ...therapyReports,
        // ...screeningReports,
        // ...therapyReportResponse,
      ]
    }

    if (allReports.length === 0) {
      // Call methodCallWhenNoReportDataIsFound if provided
      // Note: In drawer context, this should not be the drawer's onClose handler
      // to prevent the drawer from closing when no data is found
      if (methodCallWhenNoReportDataIsFound) {
        methodCallWhenNoReportDataIsFound()
      }
      return
    }

    // Helper: grouping key per report. assessment-report-response is a list of AssessmentResponse objects (no top-level id); identity is assessmentReport.id.
    const getBookedEventId = (item) => {
      if (item?.apiParam === 'meta-prescription') {
        return item?.prescriptionData?.bookedEventId
      }
      if (item?.type === 'therapy-report-response') {
        return item?.therapyReport?.bookedEventId
      }
      if (item?.type === 'screening-report-response') {
        return item?.screeningReport?.bookedEventId
      }
      if (item?.type === 'assessment-report-response') {
        const reportId = item?.assessmentReport?.id
        return reportId != null ? `assessment-${reportId}` : null
      }
      return null
    }

    const dateMapLocal = {}
    
    // First pass: Group reports by date and bookedEventId to deduplicate
    const reportsByDateAndEvent = {}
    allReports.forEach((item) => {
      let timestamp = item.createdTime || item.modifiedTime || dayjs().toISOString()
      if (item?.apiParam === 'meta-prescription') {
        timestamp =
          item?.prescriptionData?.startTime ||
          item?.prescriptionData?.createdTime ||
          item?.prescriptionData?.modifiedTime ||
          dayjs().toISOString()
      }
      if (item?.type === 'therapy-report-response') {
        timestamp =
          item?.therapyReport?.createdTime ||
          item?.therapyReport?.modifiedTime ||
          dayjs().toISOString()
      }
      if (item?.type === 'screening-report-response') {
        timestamp =
          item?.screeningReport?.createdTime ||
          item?.screeningReport?.modifiedTime ||
          dayjs().toISOString()
      }
      if (item?.type === 'assessment-report-response') {
        const runs = item?.assessmentRuns ?? item?.assessmentReport?.assessmentRuns ?? []
        const latestRun = runs.length > 0
          ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0]
          : null
        timestamp =
          latestRun?.completedAt ||
          item?.assessmentReport?.createdTime ||
          item?.assessmentReport?.modifiedTime ||
          dayjs().toISOString()
      }

      const parsed = parseReportTimestamp(timestamp)
      const dateMoment = parsed?.isValid() ? parsed : dayjs.utc(timestamp)
      const localDate = dateMoment.local().format('DD/MM/YYYY, dddd')
      const bookedEventId = getBookedEventId(item)
      
      // Only group if bookedEventId exists, otherwise treat as separate reports
      if (bookedEventId) {
        if (!reportsByDateAndEvent[localDate]) {
          reportsByDateAndEvent[localDate] = {}
        }
        if (!reportsByDateAndEvent[localDate][bookedEventId]) {
          reportsByDateAndEvent[localDate][bookedEventId] = []
        }
        reportsByDateAndEvent[localDate][bookedEventId].push(item)
      } else {
        // Reports without bookedEventId are added directly
        if (!dateMapLocal[localDate]) {
          dateMapLocal[localDate] = []
        }
        dateMapLocal[localDate].push(item)
      }
    })

    // Second pass: Merge prescriptions only (combine images). Assessments: merge only when same report id (e.g. API sent one element per run); otherwise one entry per assessment report.
    Object.keys(reportsByDateAndEvent).forEach((localDate) => {
      if (!dateMapLocal[localDate]) {
        dateMapLocal[localDate] = []
      }
      Object.keys(reportsByDateAndEvent[localDate]).forEach((groupKey) => {
        const reportsForEvent = reportsByDateAndEvent[localDate][groupKey]
        if (reportsForEvent.length === 1) {
          dateMapLocal[localDate].push(reportsForEvent[0])
        } else {
          const prescriptions = reportsForEvent.filter((r) => r?.apiParam === 'meta-prescription')
          const assessments = reportsForEvent.filter((r) => r?.type === 'assessment-report-response')
          const nonPrescriptionNonAssessment = reportsForEvent.filter(
            (r) => r?.apiParam !== 'meta-prescription' && r?.type !== 'assessment-report-response',
          )
          if (prescriptions.length > 0) {
            const mainReport = prescriptions.reduce((best, current) => {
              return getPrescriptionContentScore(current) > getPrescriptionContentScore(best)
                ? current
                : best
            }, prescriptions[0])
            const allImages = []
            const allSignedImages = []
            const mergedPrescriptionData = { ...(mainReport?.prescriptionData || {}) }
            prescriptions.forEach((report) => {
              const sourceData = report?.prescriptionData || {}
              if (Array.isArray(sourceData?.uploadedImages)) allImages.push(...sourceData.uploadedImages)
              if (Array.isArray(sourceData?.uploadedSignedImages)) allSignedImages.push(...sourceData.uploadedSignedImages)

              Object.keys(sourceData).forEach((key) => {
                const incomingValue = sourceData[key]
                const existingValue = mergedPrescriptionData[key]
                if (key === 'uploadedImages' || key === 'uploadedSignedImages') return
                if (key === 'emrSectionDataList') {
                  mergedPrescriptionData[key] = mergeEmrSectionDataList(existingValue, incomingValue)
                  return
                }
                if (Array.isArray(incomingValue)) {
                  mergedPrescriptionData[key] = mergeArrayValues(existingValue, incomingValue)
                  return
                }
                if (typeof incomingValue === 'string') {
                  const existingLen =
                    typeof existingValue === 'string' ? existingValue.trim().length : 0
                  const incomingLen = incomingValue.trim().length
                  if (incomingLen > existingLen) {
                    mergedPrescriptionData[key] = incomingValue
                  }
                  return
                }
                if (!isNonEmptyValue(existingValue) && isNonEmptyValue(incomingValue)) {
                  mergedPrescriptionData[key] = incomingValue
                }
              })
            })
            dateMapLocal[localDate].push({
              ...mainReport,
              prescriptionData: {
                ...mergedPrescriptionData,
                uploadedImages: Array.from(new Set(allImages)),
                uploadedSignedImages: Array.from(new Set(allSignedImages)),
              },
            })
          }
          // Assessments: group by assessmentReport.id and merge runs when same report (groupKey is already assessment-${reportId})
          if (assessments.length > 0) {
            const byReportId = {}
            assessments.forEach((item) => {
              const reportId = item?.assessmentReport?.id
              const runs = item?.assessmentRuns ?? item?.assessmentReport?.assessmentRuns ?? []
              if (reportId == null) {
                dateMapLocal[localDate].push(item)
                return
              }
              if (!byReportId[reportId]) {
                byReportId[reportId] = { item, runs: [] }
              }
              byReportId[reportId].runs.push(...runs)
            })
            Object.values(byReportId).forEach(({ item, runs }) => {
              const seen = new Set()
              const deduped = runs.filter((r) => {
                const id = r?.id ?? r?.assessmentRunId
                if (id && seen.has(id)) return false
                if (id) seen.add(id)
                return true
              })
              dateMapLocal[localDate].push({
                ...item,
                assessmentReport: item?.assessmentReport,
                assessmentRuns: deduped,
                type: 'assessment-report-response',
              })
            })
          }
          nonPrescriptionNonAssessment.forEach((report) => dateMapLocal[localDate].push(report))
        }
      })
    })

    // Assessment report: one API item = one list entry. Each report contains its runs; do not expand to one entry per run.
    // (See docs/portals/mentor/assessments/patient-assessment-history.md and emr-reports-unified-client-integration.md.)

    // Sort within each date by time descending (newest first)
    const getReportTimestampLocal = (report) => {
      if (report?.apiParam === 'meta-prescription') return report?.prescriptionData?.startTime || report?.prescriptionData?.createdTime || report?.prescriptionData?.modifiedTime || ''
      if (report?.type === 'therapy-report-response') return report?.therapyReport?.createdTime || report?.therapyReport?.modifiedTime || ''
      if (report?.type === 'assessment-report-response') {
        const runs = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
        const latestRun = runs.length > 0
          ? [...runs].sort((a, b) => (parseReportTimestamp(b?.completedAt)?.valueOf() ?? 0) - (parseReportTimestamp(a?.completedAt)?.valueOf() ?? 0))[0]
          : null
        return latestRun?.completedAt || report?.assessmentReport?.createdTime || report?.assessmentReport?.modifiedTime || ''
      }
      if (report?.type === 'screening-report-response') return report?.screeningReport?.createdTime || report?.screeningReport?.modifiedTime || ''
      return report?.createdTime || report?.modifiedTime || ''
    }
    Object.keys(dateMapLocal).forEach((date) => {
      dateMapLocal[date] = [...(dateMapLocal[date] || [])].sort((a, b) => {
        const ta = parseReportTimestamp(getReportTimestampLocal(a))?.valueOf() ?? 0
        const tb = parseReportTimestamp(getReportTimestampLocal(b))?.valueOf() ?? 0
        return tb - ta
      })
    })

    setDateMap(dateMapLocal)
  }

  // async function setupThings() {
  //   setLoading(true)

  //   let uri = `/api/secure/patient/reports/list?patientId=${patientId}`
  //   if (referenceBookedEventId) {
  //     uri += `&referenceBookedEventId=${referenceBookedEventId}`
  //   }
  //   const response = await getMPDataWithoutParams(uri)
  //   setLoading(false)
  //   if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
  //     setDatewiseMapData(response.data.data)
  //   } else {
  //     toast.error('Error getting patient reports')
  //   }
  // }

  useEffect(() => {
    if (pastReportData) {
      setDatewiseMapData(pastReportData)
    }
  }, [pastReportData])

  useEffect(() => {
    if (!patientId) return
    const allEventIds = Object.values(dateMap || {})
      .flat()
      .map((report) => getBookedEventIdForReport(report))
      .filter(Boolean)
    const uniqueEventIds = [...new Set(allEventIds)]
    if (uniqueEventIds.length === 0) {
      setVitalsByBookedEventId({})
      setBatchedVitalsReady(true)
      return
    }

    const bookedEventIdsCsv = uniqueEventIds.join(',')
    const requestKey = `${patientId}::${bookedEventIdsCsv}`

    if (batchedVitalsCache.has(requestKey)) {
      setVitalsByBookedEventId(batchedVitalsCache.get(requestKey))
      setBatchedVitalsReady(true)
      return
    }

    const runRequest = async () => {
      setBatchedVitalsLoading(true)
      setBatchedVitalsReady(false)
      try {
        let requestPromise = batchedVitalsInFlightRequests.get(requestKey)
        if (!requestPromise) {
          requestPromise = getDataWithParams('/api/secure/patient/vitals', {
            patientId,
            bookedEventIds: bookedEventIdsCsv,
          })
          batchedVitalsInFlightRequests.set(requestKey, requestPromise)
        }

        const resp = await requestPromise
        const vitalsList =
          resp?.status === 200 && isCommonResponseSuccessfulV2(resp)
            ? resp?.data?.data?.vital || []
            : []
        const byEventId = {}
        if (Array.isArray(vitalsList)) {
          vitalsList.forEach((vitalItem) => {
            if (vitalItem?.bookedEventId) {
              byEventId[vitalItem.bookedEventId] = vitalItem
            }
          })
        }
        batchedVitalsCache.set(requestKey, byEventId)
        setVitalsByBookedEventId(byEventId)
      } catch (err) {
        setVitalsByBookedEventId({})
      } finally {
        batchedVitalsInFlightRequests.delete(requestKey)
        setBatchedVitalsLoading(false)
        setBatchedVitalsReady(true)
      }
    }

    runRequest()
  }, [patientId, dateMap])

  useEffect(() => {
    // if (pastReportStoreData?.status !== REDUX_STATUS_IDLE) {
    //   return
    // }
    if (!patientId) return
    dispatch(
      getPastPatientReports({
        patientId: patientId,
        referenceBookedEventId: referenceBookedEventId,
        patientHistoryGetApiSignature: patientHistoryGetApiSignature,
      }),
    )
  }, [patientId, referenceBookedEventId, patientHistoryGetApiSignature, dispatch])

  const renderIndexContent = useCallback(
    (onItemClick) =>
      groupedByVisit.map((visit) => {
        const dateStr = visit.date?.split(',')[0] || ''
        const parsed = dayjs(dateStr, 'DD/MM/YYYY')
        const dateLabel = parsed.isValid() ? parsed.format('D MMM YYYY') : visit.date
        const visitDateLine = `Visit ${visit.visitNo} · ${dateLabel}`
        return (
          <Box
            key={visit.date}
            sx={{
              mb: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.25,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.75,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                borderLeft: '3px solid',
                borderLeftColor: 'primary.main',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: 'primary.main',
                  letterSpacing: '0.02em',
                }}
              >
                {visitDateLine}
              </Typography>
            </Box>
            {visit.entries.map((entry) => {
              const typeColor = INDEX_TYPE_COLORS[entry.eventType] || 'text.secondary'
              const isActive = activeSectionId === entry.id
              return (
                <Box
                  key={entry.id}
                  onClick={() => {
                    const el = document.getElementById(entry.id)
                    if (el && contentScrollRef.current) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                    onItemClick?.()
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    pl: 1.25,
                    pr: 1,
                    py: 0.65,
                    borderLeft: '3px solid',
                    borderColor: isActive ? 'primary.main' : typeColor,
                    borderLeftColor: isActive ? 'primary.main' : typeColor,
                    bgcolor: isActive ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    opacity: isActive ? 1 : 0.92,
                    '&:hover': {
                      bgcolor: isActive ? 'action.selected' : 'action.hover',
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: typeColor,
                      flexShrink: 0,
                      mt: 0.75,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: typeColor,
                      }}
                    >
                      {entry.eventTypeLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.68rem', mt: 0.15 }}>
                      {entry.practitionerName}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )
      }),
    [groupedByVisit, activeSectionId]
  )

  return (
    <>
      {' '}
      {loading ? (
        <>
          {customLoader
            ? customLoader
            : Array.from({ length: skeletonNumber }, () => 1).map((_, index) => (
                <React.Fragment key={`skeleton-${index}`}>
                  <LSPastReportsSkeleton
                    variant="rectangular"
                    animation="wave"
                    width="100%"
                    height={200}
                    sx={{
                      '&.MuiSkeleton-wave::after': {
                        background:
                          'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.2), transparent)', // Darker wave
                        opacity: 0.8, // Increase visibility
                      },
                    }}
                  />
                  <div style={{ height: '24px' }}></div>
                </React.Fragment>
              ))}
        </>
      ) : (
        <div
          className="d-flex flex-column w-100"
          style={{ marginTop: addTopMargins ? '20px' : '0px', flex: 1, minHeight: 0 }}
        >
          {Object.keys(dateMap || {}).length === 0 ? (
            <>
              {!hideVisitSummary && computedPatientVisitSummary && (
                <Box sx={{ mb: 3, px: { xs: 2, sm: 3 }, pt: 2 }}>
                  <PatientVisitSummaryHandler
                    patientVisitSummary={computedPatientVisitSummary}
                    role={propRole}
                  />
                </Box>
              )}
              <div className="d-flex flex-column align-items-center justify-content-center py-5">
                <div className="text-muted text-center">
                  <h5 className="mb-2">No Past Reports Available</h5>
                  <p className="mb-0">There are no past visit reports to display for this patient.</p>
                </div>
              </div>
            </>
          ) : (
            (() => {
              const content = (
                <>
                  {/* Gallery view (mobile): swipe + pinch-zoom through the entire history.
                      Hidden when the parent renders its own gallery button in the drawer header. */}
                  {!hideInlineGalleryButton && isNarrowViewport && sortedDatesList.length > 0 && (
                    <Box
                      className="hide-on-print-only"
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                        px: { xs: 1.5, sm: 3 },
                        py: 0.75,
                        background: 'rgba(255, 255, 255, 0.92)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        borderBottom: '0.5px solid rgba(15, 23, 42, 0.06)',
                      }}
                    >
                      <Typography sx={{ fontSize: '12px', color: 'text.secondary', mr: 0.25 }}>
                        Gallery view
                      </Typography>
                      <IconButton
                        aria-label="Open gallery view"
                        size="small"
                        onClick={openGallery}
                        sx={{
                          color: '#6779e3',
                          backgroundColor: 'rgba(103, 121, 227, 0.1)',
                          border: '1px solid rgba(103, 121, 227, 0.25)',
                          '&:hover': { backgroundColor: 'rgba(103, 121, 227, 0.2)' },
                        }}
                      >
                        <CollectionsIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  )}
                  {!hideVisitSummary && computedPatientVisitSummary && (
                    <Box sx={{ mb: 3, px: { xs: 2, sm: 3 }, pt: 2 }}>
                      <PatientVisitSummaryHandler
                        patientVisitSummary={computedPatientVisitSummary}
                        role={propRole}
                      />
                    </Box>
                  )}
                  {sortedDatesList.length === 0 ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5">
                      <div className="text-muted text-center">
                        <h5 className="mb-2">No Past Reports Available</h5>
                        <p className="mb-0">There are no past visit reports to display for this patient.</p>
                      </div>
                    </div>
                  ) : sortedDatesList.map((date, index) => (
                    <React.Fragment key={date}>
                      {renderVisitReportsForDate(date, index, sortedDatesList.length)}
                    </React.Fragment>
                  ))}
                </>
              )
              const showSidebar = showIndexSidebar && indexEntries.length > 0
              if (showSidebar) {
                return (
                  <Box sx={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', height: '100%', overflow: 'hidden', position: 'relative' }}>
                    {!isFloatingMode && (
                      <Box
                        className="hide-on-print-only"
                        sx={{
                          flexShrink: 0,
                          width: SIDEBAR_WIDTH,
                          borderRight: '1px solid',
                          borderColor: 'divider',
                          overflowY: 'auto',
                          bgcolor: 'grey.50',
                          py: 1,
                          px: 0.5,
                        }}
                      >
                        {renderIndexContent()}
                      </Box>
                    )}
                    {isFloatingMode && (
                      <>
                        <IconButton
                          className="hide-on-print-only"
                          onClick={() => setIndexDrawerOpen(true)}
                          sx={{
                            position: 'absolute',
                            left: 8,
                            bottom: 16,
                            zIndex: 10,
                            bgcolor: 'background.paper',
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                          size="medium"
                          aria-label="Open visit index"
                        >
                          <MenuIcon />
                        </IconButton>
                        <Drawer
                          anchor="left"
                          open={indexDrawerOpen}
                          onClose={() => setIndexDrawerOpen(false)}
                          PaperProps={{
                            sx: { width: Math.min(320, typeof window !== 'undefined' ? window.innerWidth * 0.85 : 320), maxWidth: '85vw' },
                          }}
                        >
                          <Box sx={{ p: 1.5, overflowY: 'auto', height: '100%' }}>
                            {renderIndexContent(() => setIndexDrawerOpen(false))}
                          </Box>
                        </Drawer>
                      </>
                    )}
                    <Box
                      ref={contentScrollRef}
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minWidth: 0,
                      }}
                    >
                      <div className="d-flex flex-column w-100" style={{ marginTop: addTopMargins ? 20 : 0, padding: 24 }}>
                        {content}
                      </div>
                    </Box>
                  </Box>
                )
              }
              return (
                <div className="d-flex flex-column w-100" style={{ marginTop: addTopMargins ? 20 : 0 }}>
                  {content}
                </div>
              )
            })()
          )}
        </div>
      )}
      {galleryOpen && sortedDatesList.length > 0 && (
        <Dialog
          fullScreen
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          sx={galleryZIndex ? { zIndex: galleryZIndex } : undefined}
          PaperProps={{ sx: { display: 'flex', flexDirection: 'column', bgcolor: '#1b1f24' } }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
              bgcolor: '#0f1216',
              color: '#ffffff',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <CollectionsIcon sx={{ fontSize: 20 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '15px' }} noWrap>
                {`Visit ${sortedDatesList.length - galleryIndex} · ${(sortedDatesList[galleryIndex] || '').split(',')[0]}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography sx={{ fontSize: '12px', opacity: 0.7 }}>
                {galleryIndex + 1} / {sortedDatesList.length}
              </Typography>
              <IconButton onClick={() => setGalleryOpen(false)} sx={{ color: '#ffffff' }} aria-label="Close gallery">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <Swiper
              modules={[Zoom]}
              zoom={{ maxRatio: 4 }}
              spaceBetween={12}
              initialSlide={galleryIndex}
              onSlideChange={(s) => {
                setGalleryIndex(s.activeIndex)
                setShowSwipeHint(false)
              }}
              style={{ width: '100%', height: '100%' }}
            >
              {sortedDatesList.map((date, index) => (
                <SwiperSlide key={date} style={{ overflowY: 'auto', display: 'block' }}>
                  <div className="swiper-zoom-container" style={{ alignItems: 'flex-start', width: '100%' }}>
                    <Box sx={{ width: '100%', maxWidth: '100%', bgcolor: '#ffffff', borderRadius: 1, p: 1.25, my: 1 }}>
                      {renderVisitReportsForDate(date, index, sortedDatesList.length)}
                    </Box>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {sortedDatesList.length > 1 && galleryIndex > 0 && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  zIndex: 19,
                  pointerEvents: 'none',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(15, 18, 22, 0.55)',
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
                  opacity: showSwipeHint ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                  '@keyframes lsNudgeLeft': {
                    '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
                    '50%': { transform: 'translateY(-50%) translateX(-5px)' },
                  },
                  transform: 'translateY(-50%)',
                  animation: showSwipeHint ? 'lsNudgeLeft 1.4s ease-in-out infinite' : 'none',
                }}
              >
                <ChevronLeftIcon />
              </Box>
            )}
            {sortedDatesList.length > 1 && galleryIndex < sortedDatesList.length - 1 && (
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  zIndex: 19,
                  pointerEvents: 'none',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(15, 18, 22, 0.55)',
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.35)',
                  opacity: showSwipeHint ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                  '@keyframes lsNudgeRight': {
                    '0%, 100%': { transform: 'translateY(-50%) translateX(0)' },
                    '50%': { transform: 'translateY(-50%) translateX(5px)' },
                  },
                  transform: 'translateY(-50%)',
                  animation: showSwipeHint ? 'lsNudgeRight 1.4s ease-in-out infinite' : 'none',
                }}
              >
                <ChevronRightIcon />
              </Box>
            )}
            {sortedDatesList.length > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
                  transform: 'translateX(-50%)',
                  zIndex: 20,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  px: 1.75,
                  py: 0.875,
                  borderRadius: '999px',
                  bgcolor: 'rgba(15, 18, 22, 0.82)',
                  color: '#ffffff',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                  whiteSpace: 'nowrap',
                  fontSize: '12.5px',
                  fontWeight: 500,
                  opacity: showSwipeHint ? 1 : 0,
                  transition: 'opacity 0.45s ease',
                }}
              >
                <SwipeIcon sx={{ fontSize: 18 }} />
                Swipe to browse · pinch to zoom
              </Box>
            )}
          </Box>
        </Dialog>
      )}
    </>
  )
}
